// ═══════════════════════════════════════════════════════════════════════════════
// Break-glass unlock: super-admin login (skip 2FA) → maintenance settings only
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ADMIN_ROLE } from '@fdh/shared';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useMaintenance } from '../../contexts/MaintenanceContext';
import { AdminMaintenanceForm } from './AdminMaintenanceForm';

export const BreakGlassMaintenancePage = () => {
  const { login, logout, isAuthenticated, isLoading, admin } = useAdminAuth();
  const {
    active: maintenanceActive,
    isLoading: maintenanceLoading,
    refresh: refreshMaintenance,
  } = useMaintenance();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isBreakGlassSession = admin?.breakGlassSession === true;
  const canEdit = isAuthenticated && admin?.role === ADMIN_ROLE.SUPER_ADMIN;

  const handleMaintenanceSaved = useCallback(() => {
    void refreshMaintenance();
  }, [refreshMaintenance]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setSubmitting(true);
      try {
        const result = await login(email.trim(), password, undefined, { breakGlass: true });
        if (result.status === 'requires_two_factor') {
          setError('Unexpected 2FA challenge on break-glass login.');
          return;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed');
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, login],
  );

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  if (isLoading || maintenanceLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-100">
        Loading…
      </div>
    );
  }

  // Break-glass is only available while maintenance is active.
  if (!maintenanceActive) {
    return <Navigate to="/" replace />;
  }

  if (!isAuthenticated || !canEdit) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 text-slate-100">
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="w-full max-w-md space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-6 shadow-xl"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-400/90">
              Break-glass
            </p>
            <h1 className="mt-1 text-xl font-semibold text-white">Maintenance unlock</h1>
            <p className="mt-2 text-sm text-slate-400">
              Super-admin only. Email 2FA is skipped. Password is still required.
            </p>
          </div>
          {error ? (
            <p className="rounded-md border border-rose-800 bg-rose-950/40 px-3 py-2 text-sm text-rose-200" role="alert">
              {error}
            </p>
          ) : null}
          <label className="block text-sm">
            <span className="text-slate-400">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2"
              autoComplete="username"
              required
              aria-label="Super admin email"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-400">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2"
              autoComplete="current-password"
              required
              aria-label="Super admin password"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Unlock maintenance settings'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8 text-slate-100">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-400/90">
              Break-glass
            </p>
            <h1 className="mt-1 text-xl font-semibold text-white">Maintenance settings</h1>
            <p className="mt-1 text-sm text-slate-400">{admin?.email}</p>
            {isBreakGlassSession ? (
              <p className="mt-2 text-xs text-amber-200/80">
                Restricted session — maintenance settings only.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void handleLogout()}
            className="rounded-md border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-900"
            aria-label="Log out"
          >
            Log out
          </button>
        </div>
        <AdminMaintenanceForm variant="breakGlass" onSaved={handleMaintenanceSaved} />
      </div>
    </div>
  );
};
